<script>
  import { onMount } from "svelte";
  import { puzzlePack } from "../../models/puzzlePack.js";
  import { subscription } from "../../models/subscription.js";
  import { user } from "../../models/user.js";
  import { formatPrice } from "../../services/utilities.js";
  import { cart } from "../../models/cart.js";
  import Popup from "../../components/Popup.svelte";
  import SubscriptionOptions from "../../components/SubscriptionOptions.svelte";

  let howItWorksSteps = [
    {
      img: "/assets/step1.svg",
      mainText: "Select a plan",
      subText: "Add a subscription plan or a puzzle pack to your cart.",
    },
    {
      img: "/assets/step2.svg",
      mainText: "Select puzzles",
      subText:
        "Browse rentable puzzles and add the ones you want to your order.",
    },
    {
      img: "/assets/step3.svg",
      mainText: "Puzzle!",
      subText: "Receive your order and begin puzzling.",
    },
    {
      img: "/assets/step4.svg",
      mainText: "Return puzzles",
      subText:
        "After you have finished your puzzles, ship them back using the provided shipping label.",
    },
  ];

  let puzzlePackContent = {
    "Mini Puzzle Pack": {
      img: "/assets/mini-puzzle-pack.png",
      desc: "Ideal for puzzlers that do one puzzle per month",
      steps: [
        "Rent up to 2 mid-size puzzles",
        "Or 1 large puzzle (1,500 pieces or more)",
        "60 days to complete your puzzles",
      ],
      product: null,
    },
    "Puzzle Pack": {
      img: "/assets/puzzle-pack.png",
      desc: "Ideal for puzzlers of varying speeds",
      steps: [
        "Rent up to 4 mid-size puzzles",
        "Or up to 3 large puzzle (1,500 pieces or more)",
        "60 days to complete your puzzles",
      ],
      product: null,
    },
  };

  let username = null;
  let needsAuth = false;
  let errorMsg;
  let moreDetails;

  onMount(async () => {
    document.title = 'Puzzle Rental Plans & Subscriptions | Missing Piece Puzzle Exchange'
    username = await user.getUsername();
  });

  function clearError() {
    errorMsg = null;
    needsAuth = false;
  }

  function resetDetails() {
    moreDetails = null;
  }

  function addPPToCart(product) {
    if ($subscription.hasSubscription) {
      errorMsg =
        "You already have an active subscription plan. View your subscription details <a href='/account'>here</a>.";
    } else if (cart.hasMembershipInCart()) {
      errorMsg =
        "Please remove the subscription plan from your cart, if you would like to buy a puzzle pack.";
    } else {
      if (!cart.hasPuzzlePack()) {
        cart.add(product);
      }
      if (product.title.toLowerCase().indexOf("mini") > -1) {
        window.location = "/rent?referrer=added-mini-puzzlepack";
      } else {
        window.location = "/rent?referrer=added-puzzlepack";
      }
    }
  }
</script>

{#if errorMsg}
  <Popup message={errorMsg} close={() => clearError()} {needsAuth} />
{/if}

{#if moreDetails}
  <Popup message={moreDetails} close={() => resetDetails()} />
{/if}

<svelte:head>
  <title>Puzzle Rental Plans & Subscriptions | Missing Piece Puzzle Exchange</title>
  <meta name="description" content="Choose from one-time puzzle packs or monthly puzzle subscription plans. Rent jigsaw puzzles shipped free to your door — swap whenever you're done." />
  <meta property="og:title" content="Puzzle Rental Plans & Subscriptions | Missing Piece Puzzle Exchange" />
  <meta property="og:description" content="Choose from one-time puzzle packs or monthly puzzle subscription plans. Rent jigsaw puzzles shipped free to your door — swap whenever you're done." />
  <meta property="og:url" content="https://www.puzzleexchange.com/ways-to-rent" />
  <link rel="canonical" href="https://www.puzzleexchange.com/ways-to-rent" />
</svelte:head>

<div class="container">
  <section class="wtr-header">
    <div class="wtr-header-text">
      <div class="page-header-title">
        <span>Exchange puzzles</span>
        <br />
        <span>every month</span>
      </div>
      <div class="section-header-text">
        <p>
          Save money and puzzle more when renting puzzles through the Puzzle
          Exchange.
        </p>
      </div>
      <div class="hero-img">
        <img
          src="/assets/hero-puzzle-1.svg"
          alt="Save money and puzzle more when renting puzzles through the
          Puzzle Exchange either via a monthly subscription or a one time
          rental."
        />
      </div>
    </div>
  </section>

  <section class="wtr-options">
    <div class="wtr-options-text section-header-text">
      <p class="section-title">Choose your puzzle plan</p>
      <span class="smaller-text">
        No time limit to complete the puzzle as long as you remain an active
        member. No puzzle size restrictions. No commitment, cancel anytime after
        puzzles are returned. Return shipping label included.
      </span>
    </div>

    <!--Subscription Plan Options-->
    <SubscriptionOptions />
  </section>

  <!--Puzzle Pack Options-->
  <section class="background-white pay-as-you-go">
    <div class="section-header-text">
      <span class="smaller-text">or</span>
      <p class="section-title">Pay as you go</p>
      <span class="smaller-text">
        Try a few puzzles. Pay as you go. No subscription needed. Return
        shipping label included.
      </span>
    </div>
    <div class="wtr-options-pp">
      {#each Object.values($puzzlePack.options) as pp}
        <div class="wtr-options-pp-card">
          <div class="card-img">
            <img
              src={puzzlePackContent[pp.title].img}
              alt={pp.title + puzzlePackContent[pp.title].desc}
            />
          </div>
          <p class="membership-title">{pp.title}</p>
          <p class="price">{formatPrice(pp.price, true)}</p>
          <p class="card-subtext">{puzzlePackContent[pp.title].desc}</p>
          {#each puzzlePackContent[pp.title].steps as step}
            <div class="wtr-checkbox-steps">
              <img src="/assets/checkbox-circle.svg" alt={step} />
              <span>{step}</span>
            </div>
          {/each}
          <button
            class="btn wtr-btn"
            on:click|preventDefault={() => addPPToCart(pp)}
          >
            Add to cart
          </button>
        </div>
      {/each}
    </div>

    <div class="buy-section">
      <div class="details">
        <p class="membership-title">Buy</p>
        <span>
          starting at
          <p class="price">$10</p>
        </span>
        <p>
          Great for those who prefer to own their puzzles and build their
          collection.
        </p>
        <p>Over 200 puzzles to choose from.</p>
        <a class="link" href="/shop">
          <button class="btn wtr-btn">Shop puzzles</button>
        </a>
      </div>
      <div>
        <img src="/assets/puzzle-thumbnails.png" alt="buy jigsaw puzzles" />
      </div>
    </div>
  </section>

  <!--How it works section-->
  <section>
    <div class="section-header-text">
      <p class="section-title">How it works</p>
      <span class="smaller-text">
        Puzzle when you're ready with puzzle plans and packs.
      </span>
    </div>
    <div class="wtr-how-it-works-steps">
      {#each howItWorksSteps as step}
        <div class="wtr-how-it-works-step">
          <img src={step.img} alt={step.mainText + step.subText} />
          <div>
            <h5>{step.mainText}</h5>
            <span class="smaller-text">{step.subText}</span>
          </div>
        </div>
      {/each}
    </div>
  </section>

  <!--Gift a gift section-->
  <section class="background-white wtr-gift">
    <div class="section-header-text">
      <p class="section-title">Give a gift</p>
      <p class="smaller-text">
        Whether it's a birthday, holiday, or any day, give the gift of puzzling.
      </p>
    </div>
    <div class="section-content">
      <img
        alt="gift a puzzle subscription"
        style="width: 64px; height:96px;"
        src="/assets/puzzle-friends.svg"
      />
      <br />
      <a class="link" href="/gift-cards">
        <button class="btn wtr-btn">Shop gift cards</button>
      </a>
      <p class="smaller-text">
        Received a gift? <a href="/redeem">Here's how to redeem it.</a>
      </p>
    </div>
  </section>

  <!--Visit Us In Store section-->
  <section class="background-white wtr-gift">
    <div class="section-header-text">
      <p class="section-title">Visit us in store</p>
      <p class="smaller-text">
        <a target="_blank" href="https://maps.app.goo.gl/yWhUBTFStotKYYzR8">
          7188 Lowell Blvd, Westminster, CO 80030
        </a>
      </p>
    </div>
  </section>
</div>

<style type="text/less">
  img {
    max-width: 100%;
  }

  .container {
    margin: auto;
    display: grid;
  }

  .hero {
    width: 100%;
  }

  .page-header-title {
    font-weight: 500;
    line-height: 60px;
    letter-spacing: 2px;
    text-align: center;
    margin: 0 auto;
  }

  section {
    padding-top: 2rem;
    padding-bottom: 2rem;
    text-align: center;
  }

  .section-header-text {
    margin: 0 auto;
    text-align: center;
    width: 50%;
  }
  .section-content {
    text-align: center;
  }

  .section-title {
    font-size: 32px;
    font-weight: 400;
    line-height: 38px;
  }

  .pay-as-you-go-title {
    font-size: 28px;
    line-height: 32px;
  }

  .wtr-how-it-works-steps {
    width: 40%;
    margin: 0 auto;
    text-align: left;
    padding-top: 2rem;
  }

  @media (max-width: 1150px) {
    .page-header-title {
      font-size: 38px;
    }
    .wtr-header {
      height: 315px;
    }
    .membership-title {
      font-size: 30px;
      line-height: 32px;
    }
    .section-header-text {
      padding: 1rem;
      width: 90%;
    }

    button {
      width: 100%;
    }
    .wtr-how-it-works-steps {
      width: 90%;
      padding: 1rem;
    }

    .wtr-options {
      .wtr-options-memberships {
        display: grid;
        grid-template-columns: repeat(1, 1fr);
        margin: 0 auto;
        justify-items: center;
        grid-gap: 2rem;
        justify-content: center;
        padding-top: 1rem;
      }

      .wtr-options-membership-img {
        img {
          width: 100px;
          height: 100px;
        }
      }
    }
    .wtr-options-pp {
      display: grid;
      grid-template-columns: repeat(1, 1fr);
      justify-items: center;
      align-items: center;
      margin: 0 auto;
      padding: 0;
      padding-bottom: 2rem;

      .card-img {
        text-align: center;
      }
    }

    .wtr-options-pp-card {
      width: 85%;
    }

    .wtr-gift {
      padding: 2rem;
    }

    .buy-section {
      flex-direction: column-reverse;
      width: 85%;

      .details {
        width: 100%;
      }
    }

    .wtr-btn {
      width: 100%;
    }

    .wtr-how-it-works-step {
      padding-bottom: 1rem;
    }
  }

  /* non mobile specific css */
  @media (min-width: 1150px) {
    .page-header-title {
      font-size: 48px;
    }
    .wtr-header {
      height: 250px;
      .hero-img {
        top: 200px;
      }
    }
    .membership-title {
      font-size: 36px;
      line-height: 60px;
    }
    .wtr-options {
      .wtr-options-memberships {
        padding-top: 1rem;
        display: flex;
        justify-content: center;
        margin: 0 auto;
        column-gap: 2rem;
      }

      .wtr-options-membership-img {
        img {
          width: 150px;
          height: 150px;
        }
      }
    }
    .wtr-options-pp {
      display: flex;
      justify-content: space-evenly;
      margin: 0 auto 3rem auto;
    }

    .buy-section {
      width: 75%;
      .details {
        width: 50%;
      }
    }

    .wtr-btn {
      width: 200px;
    }

    .wtr-how-it-works-step {
      padding-bottom: 2rem;
    }
  }

  .wtr-how-it-works-step {
    display: flex;
    column-gap: 2rem;

    img {
      width: 50px;
      height: 50px;
    }
  }

  .wtr-header {
    color: var(--col-accent-2);
    background-color: var(--col-accent-1-xl);

    .hero-img {
      position: absolute;

      img {
        width: 200px;
      }
    }
  }

  .wtr-options {
    .wtr-options-memberships {
      .popular {
        background-color: var(--col-accent-2-d);
        color: white;

        button {
          background-color: var(--col-accent-1);
        }

        .price {
          .num {
            color: white;
          }
        }
      }

      .popular-header {
        background-color: var(--col-accent-2-l2);
        margin-top: -33px;
        margin-left: -16px;
        padding: 8px 24px 8px 24px;
        width: 284px;
        border-radius: 16px 16px 0 0;
        text-align: center;
        text-transform: uppercase;
        font-size: 12px;
      }
    }

    .wtr-options-membership {
      padding: 1rem;
      background-color: white;
      width: 300px;
      border-radius: 16px;
      text-align: left;
      box-shadow: 0px 10px 5px 0px rgba(154, 160, 185, 0.05);
      box-shadow: 0px 15px 40px 0px rgba(166, 173, 201, 0.2);
      z-index: 100;

      button {
        width: 100%;
      }

      .price {
        display: flex;
        height: 50px;

        .num {
          color: var(--col-accent-2);
          font-size: 28px;
          line-height: 32px;
          margin-right: 5px;
        }
        .duration {
          font-size: 16px;
          line-height: 60px;
        }
      }
    }

    .wtr-options-membership-img {
      img {
        border-radius: 99px;
        border: 3px solid var(--col-accent-1);
      }

      margin: 15px auto;
      text-align: center;
    }
  }

  .wtr-btn {
    font-weight: 500;
    line-height: 20px;
    background-color: var(--col-gray-95);
    color: black;
    border: none;
    height: 44px;
  }

  .card-subtext {
    padding: 5px 0;
  }

  .wtr-options-pp {
    .card-img {
      height: 275px;

      img {
        height: 250px;
      }
    }

    .wtr-options-pp-card {
      padding: 1rem;
      background-color: white;
      text-align: left;
      padding-bottom: 2rem;
    }
  }

  .wtr-checkbox-steps {
    padding-bottom: 15px;
  }

  .buy-section {
    border: 1px var(--col-gray-90) solid;
    border-radius: 8px;
    padding: 1rem;
    margin: 0 auto;
    text-align: left;
    display: flex;
    justify-content: space-between;

    .details {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }
  }
  .pay-as-you-go {
    margin: -3rem auto 0 auto;
    width: 100%;

    .price {
      color: var(--col-accent-2);
      font-size: 20px;
      line-height: 24px;
      display: inline;
    }
  }

  .background-white {
    background-color: hsla(0, 0%, 100%, 1);
  }

  .background-grey {
    background-color: var(--col-gray-95);
    color: black;
    border: none;
    height: 44px;
  }

  .yearly-savings {
    display: flex;
    align-items: center;
  }

  .savings {
    font-size: 12px;
    font-weight: 500;
    color: var(--col-success-d);
    border: 1px solid var(--col-success-xl);
    background-color: var(--col-success-xl);
    border-radius: 99px;
    padding: 4px 8px 4px 8px;
    height: 22px;
    width: 66px;
    text-align: center;
    line-height: 0px;
    margin-left: 10px;
  }

  .wtr-duration-toggle {
    padding-top: 2rem;
    padding-bottom: 2rem;

    .wtr-duration-toggle-inner {
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .duration {
      font-size: 18px;
      font-weight: 500;
      vertical-align: middle;
      margin-right: 10px;
      margin-left: 10px;
    }
  }

  .smaller-text {
    font-size: 14px;
  }

  /* The switch - the box around the slider */
  .switch {
    position: relative;
    display: inline-block;
    width: 60px;
    height: 34px;
  }

  /* Hide default HTML checkbox */
  .switch input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  /* The slider */
  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--col-accent-2);
    -webkit-transition: 0.4s;
    transition: 0.4s;
  }

  .slider:before {
    position: absolute;
    content: "";
    height: 26px;
    width: 26px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    -webkit-transition: 0.4s;
    transition: 0.4s;
  }

  input:checked + .slider {
    background-color: var(--col-accent-2);
  }

  input:focus + .slider {
    box-shadow: 0 0 1px var(--col-accent-2);
  }

  input:checked + .slider:before {
    -webkit-transform: translateX(26px);
    -ms-transform: translateX(26px);
    transform: translateX(26px);
  }

  /* Rounded sliders */
  .slider.round {
    border-radius: 34px;
  }

  .slider.round:before {
    border-radius: 50%;
  }

  .membership-title-header {
    display: flex;
  }

  .membership-title-header {
    .membership-info {
      height: 25px;
      width: 25px !important;
      border-radius: 50%;
      display: inline-block;
      text-align: center;
      border: solid 1px;
      align-self: center;
      margin-left: 15px;
      padding: 0;
      font-size: 15px;
    }
  }
</style>
